/**
 * Bug Report Service - API client para sistema de Ocorrencias
 *
 * Epic Bug Reports - Gestao de bugs reportados pelo offline ou online
 */
import apiClient from './apiClient';

// ========== TYPES ==========

export type Criticidade = 'BAIXA' | 'MEDIA' | 'ALTA' | 'CRITICA';
export type BugStatus = 'ABERTO' | 'EM_ANALISE' | 'EM_CORRECAO' | 'CORRIGIDO' | 'FECHADO' | 'NAO_REPRODUZIVEL';
export type Origem = 'OFFLINE' | 'ONLINE';

export interface CategoriaBugReport {
  id: number;
  nome: string;
  descricao: string | null;
  ordem: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoriaBugReportInput {
  nome: string;
  descricao?: string;
  ordem: number;
  ativo: boolean;
}

export interface BugReport {
  id: number;
  job_id: number | null;
  job_titulo: string | null;
  hospital_id: number | null;
  hospital_nome: string | null;
  reportado_por_id: number;
  reportado_por_nome: string | null;

  categoria_id: number | null;
  categoria_nome: string;
  criticidade: Criticidade;
  criticidade_display: string;

  titulo: string;
  descricao: string;
  foto_url: string | null;

  tela_atual: string | null;
  device_model: string | null;
  os_version: string | null;
  app_version: string | null;
  origem: Origem;
  origem_display: string;

  status: BugStatus;
  status_display: string;
  resolucao: string | null;
  resolvido_por_id: number | null;
  resolvido_por_nome: string | null;
  resolvido_em: string | null;

  created_at: string;
  updated_at: string;
}

export interface BugReportListResponse {
  items: BugReport[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface BugReportStats {
  total: number;
  por_criticidade: Record<Criticidade, number>;
  por_status: Record<BugStatus, number>;
  por_origem: Record<Origem, number>;
  criticos_abertos: number;
}

export interface BugReportFilters {
  page?: number;
  page_size?: number;
  criticidade?: Criticidade;
  status?: BugStatus;
  categoria_id?: number;
  origem?: Origem;
  hospital_id?: number;
  search?: string;
}

export interface BugReportUpdateInput {
  status?: BugStatus;
  resolucao?: string;
}

export interface BugReportCreateInput {
  titulo: string;
  descricao: string;
  criticidade: Criticidade;
  categoria_id?: number | null;
  categoria_nome?: string;
  job_id?: number | null;
  hospital_id?: number | null;
  tela_atual?: string;
  foto?: File | null;
}

// ========== CATEGORIAS API ==========

export const listarCategorias = async (apenas_ativas: boolean = true): Promise<CategoriaBugReport[]> => {
  const response = await apiClient.get<CategoriaBugReport[]>('/bug-reports/categorias', {
    params: { apenas_ativas },
  });
  return response.data;
};

export const criarCategoria = async (data: CategoriaBugReportInput): Promise<CategoriaBugReport> => {
  const response = await apiClient.post<CategoriaBugReport>('/bug-reports/categorias', data);
  return response.data;
};

export const atualizarCategoria = async (
  id: number,
  data: CategoriaBugReportInput,
): Promise<CategoriaBugReport> => {
  const response = await apiClient.patch<CategoriaBugReport>(
    `/bug-reports/categorias/${id}`,
    data,
  );
  return response.data;
};

export const deletarCategoria = async (id: number): Promise<void> => {
  await apiClient.delete(`/bug-reports/categorias/${id}`);
};

// ========== BUG REPORTS API ==========

export const listarBugReports = async (filters: BugReportFilters = {}): Promise<BugReportListResponse> => {
  const response = await apiClient.get<BugReportListResponse>('/bug-reports', {
    params: filters,
  });
  return response.data;
};

export const detalheBugReport = async (id: number): Promise<BugReport> => {
  const response = await apiClient.get<BugReport>(`/bug-reports/${id}`);
  return response.data;
};

export const atualizarBugReport = async (
  id: number,
  data: BugReportUpdateInput,
): Promise<BugReport> => {
  const response = await apiClient.patch<BugReport>(`/bug-reports/${id}`, data);
  return response.data;
};

export const getStats = async (): Promise<BugReportStats> => {
  const response = await apiClient.get<BugReportStats>('/bug-reports/stats');
  return response.data;
};

/**
 * Cria um bug report com upload de foto opcional (multipart/form-data).
 */
export const criarBugReport = async (data: BugReportCreateInput): Promise<BugReport> => {
  const formData = new FormData();
  formData.append('titulo', data.titulo);
  formData.append('descricao', data.descricao);
  formData.append('criticidade', data.criticidade);
  formData.append('origem', 'ONLINE');

  if (data.categoria_id != null) formData.append('categoria_id', String(data.categoria_id));
  if (data.categoria_nome) formData.append('categoria_nome', data.categoria_nome);
  if (data.job_id != null) formData.append('job_id', String(data.job_id));
  if (data.hospital_id != null) formData.append('hospital_id', String(data.hospital_id));
  if (data.tela_atual) formData.append('tela_atual', data.tela_atual);

  if (data.foto) {
    formData.append('foto', data.foto);
  }

  const response = await apiClient.post<BugReport>('/bug-reports', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// ========== HELPERS ==========

export const CRITICIDADE_COLORS: Record<Criticidade, { bg: string; text: string; border: string; dot: string }> = {
  BAIXA: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-400', dot: 'bg-gray-500' },
  MEDIA: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-500', dot: 'bg-amber-500' },
  ALTA: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-500', dot: 'bg-orange-500' },
  CRITICA: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500', dot: 'bg-red-600' },
};

export const STATUS_COLORS: Record<BugStatus, { bg: string; text: string; border: string }> = {
  ABERTO: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-400' },
  EM_ANALISE: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-400' },
  EM_CORRECAO: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-400' },
  CORRIGIDO: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-400' },
  FECHADO: { bg: 'bg-gray-200', text: 'text-gray-700', border: 'border-gray-400' },
  NAO_REPRODUZIVEL: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-400' },
};
